import { hasObjectKey } from '@core/utils';
import type { ListWelloCommentDto } from '@generated/auth';
import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { delay } from 'es-toolkit';

import { AuthApi, AuthQueryKey } from '@/api';
import type { CommentListItemPatch } from '@/api/getCommentList';
import { findCommentPage, patchCommentInListData } from '@/api/getCommentList';
import { isSameEndpointQuery } from '@/utils/isSameEndpointQuery';

// 백엔드 목록 반영(read-after-write) 지연 동안 낙관 반영을 유지하고,
// 반영이 확인된 뒤에만 목록을 실데이터로 교체한다.
export const MAX_COMMENT_PROBE_RETRIES = 5;
export const COMMENT_PROBE_DELAY_MS = 400;

export const isWelloCommentListQuery = (
  query: Query<unknown, Error, unknown, QueryKey>,
) =>
  isSameEndpointQuery(
    query,
    AuthQueryKey['get:/wello/v2/auth/wello-comment'](),
  );

// 댓글 목록은 낙관 반영이 담당하므로 무효화에서 제외한다.
// 지연 창의 refetch가 stale 목록으로 낙관 반영을 덮는 것을 막는다.
export const invalidateExceptCommentLists = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    predicate: (query) => !isWelloCommentListQuery(query),
  });

// 캐시된 모든 댓글 목록에서 해당 댓글에 낙관 패치를 적용한다(수정/삭제/신고).
export const patchCommentInAllLists = (
  queryClient: QueryClient,
  commentId: number,
  patch: CommentListItemPatch,
) => {
  queryClient.setQueriesData(
    { predicate: isWelloCommentListQuery },
    (previousData: unknown) =>
      patchCommentInListData(previousData, commentId, patch),
  );
};

type CommentListQuery = NonNullable<
  Parameters<(typeof AuthApi)['get:/wello/v2/auth/wello-comment']>[0]
>['query'];

// 대상 댓글이 들어 있는 목록 캐시를 찾아 같은 조건(컨텐츠·정렬·페이지)의 probe 파라미터를 만든다.
const findProbeQueryForComment = (
  queryClient: QueryClient,
  commentId: number,
) => {
  const commentListEntries = queryClient.getQueriesData({
    predicate: isWelloCommentListQuery,
  });

  for (const [queryKey, data] of commentListEntries) {
    const page = findCommentPage(data, commentId);

    if (page === undefined) {
      continue;
    }

    const [firstQueryKey] = queryKey;

    if (
      !firstQueryKey ||
      typeof firstQueryKey !== 'object' ||
      !hasObjectKey(firstQueryKey, 'query')
    ) {
      continue;
    }

    // 쿼리키의 query는 이 엔드포인트 요청 파라미터 원본이다.
    return { ...(firstQueryKey.query as CommentListQuery), page };
  }

  return undefined;
};

interface ReconcileCommentListsParams {
  queryClient: QueryClient;
  commentId: number;
  /** probe 응답의 대상 댓글(목록에서 사라졌으면 undefined)을 보고 서버 반영 여부를 판정한다 */
  checkReflected: (comment: ListWelloCommentDto | undefined) => boolean;
}

/**
 * 새 상태(수정/삭제/신고)가 목록 조회에 반영됐는지 캐시를 거치지 않고 확인한 뒤,
 * 반영이 확인된 경우에만 활성 목록을 refetch해 낙관 반영을 실데이터로 교체한다.
 * refetch로 확인하면 지연 창의 stale 응답이 캐시를 덮어 낙관 반영이 사라진다(깜빡임).
 */
export const reconcileCommentListsAfterChange = async ({
  queryClient,
  commentId,
  checkReflected,
}: ReconcileCommentListsParams) => {
  const probeQuery = findProbeQueryForComment(queryClient, commentId);

  if (!probeQuery) {
    return;
  }

  for (let attempt = 0; attempt < MAX_COMMENT_PROBE_RETRIES; attempt++) {
    await delay(COMMENT_PROBE_DELAY_MS);

    try {
      const { data } = await AuthApi['get:/wello/v2/auth/wello-comment']({
        query: probeQuery,
      });
      const probedComment = data?.context?.contents?.find(
        (comment) => comment.welloCommentId === commentId,
      );

      if (!checkReflected(probedComment)) {
        continue;
      }
    } catch {
      // 확인 조회가 실패하면 낙관 상태를 유지한 채 종료한다.
      return;
    }

    await queryClient.refetchQueries({
      predicate: isWelloCommentListQuery,
      type: 'active',
    });

    return;
  }
};
